<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>提现申请</span>
        <el-select v-model="filterStatus" placeholder="状态" clearable style="width: 140px" @change="handleSearch">
          <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="agentId" label="代理商ID" width="100" />
        <el-table-column prop="amount" label="金额" width="100" />
        <el-table-column label="收款方式" width="100">
          <template slot-scope="{ row }">{{ payMethodLabel(row.payMethod) }}</template>
        </el-table-column>
        <el-table-column prop="payeeName" label="收款人" width="100" show-overflow-tooltip />
        <el-table-column label="收款账号" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <template v-if="row.payMethod === 'bank'">{{ row.bankName }} {{ row.bankCardNo }}</template>
            <template v-else>—</template>
          </template>
        </el-table-column>
        <el-table-column label="收款码" width="90">
          <template slot-scope="{ row }">
            <img
              v-if="row.qrCodeUrl"
              :src="resolveFileUrl(row.qrCodeUrl)"
              alt="收款码"
              class="qr-thumb"
              @click="previewQr(row)"
            />
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">{{ labelOf(WITHDRAWAL_STATUS, row.status) }}</template>
        </el-table-column>
        <el-table-column prop="createdAt" label="申请时间" min-width="160" />
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
            <template v-if="row.status === 0">
              <el-button type="text" size="small" @click="handleProcess(row, 1)">通过</el-button>
              <el-button type="text" size="small" class="danger" @click="handleProcess(row, 2)">驳回</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </el-card>

    <el-dialog title="提现详情" :visible.sync="detailVisible" width="480px">
      <div v-if="detailRow" class="detail-body">
        <div class="detail-row"><span>提现ID</span><b>{{ detailRow.id }}</b></div>
        <div class="detail-row"><span>代理商ID</span><b>{{ detailRow.agentId }}</b></div>
        <div class="detail-row"><span>金额</span><b>¥{{ detailRow.amount }}</b></div>
        <div class="detail-row"><span>收款方式</span><b>{{ payMethodLabel(detailRow.payMethod) }}</b></div>
        <div class="detail-row"><span>收款人</span><b>{{ detailRow.payeeName || '—' }}</b></div>
        <template v-if="detailRow.payMethod === 'bank'">
          <div class="detail-row"><span>所属银行</span><b>{{ detailRow.bankName || '—' }}</b></div>
          <div class="detail-row"><span>银行卡号</span><b>{{ detailRow.bankCardNo || '—' }}</b></div>
          <div class="detail-row"><span>开户支行</span><b>{{ detailRow.openingBranch || '—' }}</b></div>
        </template>
        <div class="detail-row"><span>状态</span><b>{{ labelOf(WITHDRAWAL_STATUS, detailRow.status) }}</b></div>
        <div class="detail-row"><span>申请时间</span><b>{{ detailRow.createdAt }}</b></div>
        <div v-if="detailRow.qrCodeUrl" class="detail-qr-block">
          <span>收款二维码</span>
          <img :src="resolveFileUrl(detailRow.qrCodeUrl)" alt="收款码" class="detail-qr" @click="previewQr(detailRow)" />
        </div>
      </div>
    </el-dialog>

    <el-dialog title="收款二维码" :visible.sync="qrPreviewVisible" width="360px" append-to-body>
      <img v-if="qrPreviewUrl" :src="qrPreviewUrl" alt="收款码" class="qr-preview-full" />
    </el-dialog>
  </div>
</template>

<script>
import { getWithdrawals, processWithdrawal } from '@/api/admin'
import { WITHDRAWAL_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import { resolveFileUrl } from '@/utils/fileUrl'
import listPage from '@/mixins/listPage'

const PAY_METHOD_LABELS = { bank: '银行卡', alipay: '支付宝', wechat: '微信' }

export default {
  name: 'AgentsWithdrawal',
  mixins: [listPage],
  data() {
    return {
      WITHDRAWAL_STATUS,
      filterStatus: null,
      detailVisible: false,
      detailRow: null,
      qrPreviewVisible: false,
      qrPreviewUrl: ''
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(WITHDRAWAL_STATUS)
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    labelOf,
    resolveFileUrl,
    payMethodLabel(method) {
      return PAY_METHOD_LABELS[method] || method || '—'
    },
    fetchList(params) {
      return getWithdrawals({
        ...params,
        status: this.filterStatus != null ? this.filterStatus : undefined
      })
    },
    openDetail(row) {
      this.detailRow = row
      this.detailVisible = true
    },
    previewQr(row) {
      if (!row?.qrCodeUrl) return
      this.qrPreviewUrl = resolveFileUrl(row.qrCodeUrl)
      this.qrPreviewVisible = true
    },
    handleProcess(row, status) {
      const action = status === 1 ? '通过' : '驳回'
      this.$confirm(`确定${action}该提现申请？`, '提示', { type: 'warning' }).then(async () => {
        try {
          await processWithdrawal(row.id, status)
          this.$message.success('处理成功')
          this.loadList()
        } catch (e) {
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
.danger {
  color: #f56c6c;
}
.qr-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  cursor: pointer;
}
.detail-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
}
.detail-row span {
  color: #909399;
}
.detail-qr-block {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
}
.detail-qr {
  width: 180px;
  height: 180px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  cursor: pointer;
}
.qr-preview-full {
  width: 100%;
  max-height: 320px;
  object-fit: contain;
}
</style>
